// admin-client/src/pages/Etechlog.jsx
// =====================================================================
// eTechlog Tracking admin tab. Four sub-views:
//   - Active     — currently-checked-out eTechlogs, with overdue (>36h)
//                  rows highlighted red.
//   - Devices    — the eTechlog iPads being tracked. Create, rename,
//                  regenerate-QR, retire, download QR PNG.
//   - Trackers   — the instructor-room iPads running the scanner PWA.
//                  Same pairing-code flow as the main Devices page.
//   - History    — past checkouts, filterable.
// =====================================================================

import { useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtDateTime } from '../lib/datetime.js';
import { useAuth } from '../contexts/AuthContext.jsx';

const TABS = [
  { id: 'active',   label: 'Active checkouts' },
  { id: 'devices',  label: 'eTechlog devices' },
  { id: 'trackers', label: 'Tracker iPads' },
  { id: 'history',  label: 'History' },
];

export default function Etechlog() {
  const [tab, setTab] = useState('active');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-cathay-700">eTechlog Tracking</h1>
        <p className="text-sm text-slate-600 mt-1">
          Manage the eTechlog iPad pool in the instructor room. Each eTechlog
          carries a printed QR sticker; instructors scan it on a paired
          tracker iPad to check out / return. Sessions over 36 hours are
          automatically flagged for follow-up.
        </p>
      </header>

      <div className="flex gap-1 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px ' +
              (tab === t.id
                ? 'border-cathay-500 text-cathay-700 font-medium'
                : 'border-transparent text-slate-600 hover:text-slate-800')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'active'   && <ActiveSection />}
      {tab === 'devices'  && <DevicesSection />}
      {tab === 'trackers' && <TrackersSection />}
      {tab === 'history'  && <HistorySection />}
    </div>
  );
}

/* ---------------- Active checkouts ---------------- */

function ActiveSection() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const refresh = async () => {
    try {
      const r = await api.etechlog.checkouts({ status: 'active' });
      setRows(r.checkouts);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);
  const overdueCount = useMemo(() => rows.filter((r) => r.overdue_flagged_at).length, [rows]);
  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-slate-800">Currently out ({rows.length})</h2>
        {overdueCount > 0 && (
          <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded">
            {overdueCount} overdue (&gt;36h) — follow up
          </span>
        )}
      </div>
      {err && <div className="mt-2 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      <table className="min-w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-slate-500 text-xs">
            <th className="py-1">eTechlog</th>
            <th className="py-1">Checked out by</th>
            <th className="py-1">Name (from directory)</th>
            <th className="py-1">Checked out at</th>
            <th className="py-1">Elapsed</th>
            <th className="py-1">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const isOverdue = !!c.overdue_flagged_at;
            const elapsed = elapsedString(c.checked_out_at);
            return (
              <tr key={c.id} className={'border-t border-slate-100 ' + (isOverdue ? 'bg-red-50' : '')}>
                <td className="py-1.5 font-medium">{c.etechlog_label}</td>
                <td className="py-1.5 font-mono">{c.cathay_id}</td>
                <td className="py-1.5 text-slate-600">{c.cathay_user_display_name || '—'}</td>
                <td className="py-1.5 text-slate-600">{fmtDateTime(c.checked_out_at)}</td>
                <td className="py-1.5">{elapsed}</td>
                <td className="py-1.5">
                  {isOverdue ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800">
                      Overdue
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                      Out
                    </span>
                  )}
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr><td colSpan={6} className="py-6 text-center text-slate-500">No eTechlogs are currently out.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

function elapsedString(fromIso) {
  const t = new Date(fromIso).getTime();
  if (!Number.isFinite(t)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/* ---------------- Devices (eTechlogs being tracked) ---------------- */

function DevicesSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin || user?.role === 'manager';
  const [devices, setDevices] = useState([]);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.etechlog.devicesList();
      setDevices(r.devices);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.etechlog.devicesCreate({ label: newLabel.trim() });
      setNewLabel('');
      setShowNew(false);
      await refresh();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const rename = async (d) => {
    const label = prompt('New label for this eTechlog:', d.label);
    if (!label || label === d.label) return;
    try {
      await api.etechlog.devicesPatch(d.id, { label: label.trim() });
      await refresh();
    } catch (e) { setErr(e.message); }
  };

  const regenQr = async (d) => {
    if (!confirm(`Regenerate QR for "${d.label}"?\n\nThe currently-printed sticker will stop working immediately. Print and apply the new QR before next use.`)) return;
    try {
      await api.etechlog.devicesRegenerateQr(d.id);
      await refresh();
    } catch (e) { setErr(e.message); }
  };

  const retire = async (d) => {
    if (!confirm(`Retire "${d.label}"?\n\nIt disappears from the active list but historical checkouts are preserved. The QR will no longer scan.`)) return;
    try {
      await api.etechlog.devicesDelete(d.id);
      await refresh();
    } catch (e) { setErr(e.message); }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-slate-800">eTechlog devices ({devices.length})</h2>
        {canEdit && (
          <button
            onClick={() => setShowNew((v) => !v)}
            className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-1.5 rounded text-sm"
          >
            {showNew ? 'Cancel' : '+ New eTechlog'}
          </button>
        )}
      </div>
      {err && <div className="mt-2 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      {showNew && (
        <form onSubmit={create} className="mt-3 flex gap-2 items-end">
          <label className="flex-1">
            <span className="block text-xs text-slate-600">Label (e.g. eTechlog-A1)</span>
            <input
              required
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <button
            disabled={busy}
            className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
          >
            Create
          </button>
        </form>
      )}

      <table className="min-w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-slate-500 text-xs">
            <th className="py-1">Label</th>
            <th className="py-1">Status</th>
            <th className="py-1">Current checkout</th>
            <th className="py-1"></th>
          </tr>
        </thead>
        <tbody>
          {devices.map((d) => (
            <tr key={d.id} className="border-t border-slate-100">
              <td className="py-1.5 font-medium">{d.label}</td>
              <td className="py-1.5">
                {d.active_checkout_id ? (
                  <span className={
                    'text-xs px-2 py-0.5 rounded ' +
                    (d.active_overdue_flagged_at ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800')
                  }>
                    {d.active_overdue_flagged_at ? 'Overdue' : 'Out'}
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-800">In stock</span>
                )}
              </td>
              <td className="py-1.5 font-mono">
                {d.active_cathay_id ? `${d.active_cathay_id} · since ${fmtDateTime(d.active_checked_out_at)}` : '—'}
              </td>
              <td className="py-1.5 text-right space-x-2">
                <a
                  href={api.etechlog.deviceQrPngUrl(d.id)}
                  download={`etechlog-${d.label}.png`}
                  className="text-cathay-700 hover:underline text-xs"
                >
                  Download QR
                </a>
                {canEdit && (
                  <>
                    <button onClick={() => rename(d)} className="text-slate-700 hover:underline text-xs">Rename</button>
                    <button onClick={() => regenQr(d)} className="text-amber-700 hover:underline text-xs">Regenerate QR</button>
                    {isAdmin && (
                      <button onClick={() => retire(d)} className="text-red-600 hover:underline text-xs">Retire</button>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
          {devices.length === 0 && (
            <tr><td colSpan={4} className="py-6 text-center text-slate-500">No eTechlog devices registered yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* ---------------- Tracker iPads ---------------- */

function TrackersSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [trackers, setTrackers] = useState([]);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', location: '' });
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState(null); // { name, code }

  const refresh = async () => {
    try {
      const r = await api.etechlog.trackersList();
      setTrackers(r.trackers);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.etechlog.trackersCreate({ name: form.name.trim(), location: form.location.trim() });
      setLastCode({ name: r.tracker.name, code: r.tracker.pairing_code });
      setForm({ name: '', location: '' });
      setShowNew(false);
      await refresh();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const regen = async (t) => {
    if (!confirm('Regenerate pairing code? The existing pairing on the tracker iPad will be invalidated.')) return;
    try {
      const r = await api.etechlog.trackersRegenerateCode(t.id);
      setLastCode({ name: r.tracker.name, code: r.tracker.pairing_code });
      await refresh();
    } catch (e) { setErr(e.message); }
  };

  const revoke = async (t) => {
    if (!confirm('Revoke this tracker? It will need to be paired again.')) return;
    try { await api.etechlog.trackersRevoke(t.id); await refresh(); }
    catch (e) { setErr(e.message); }
  };

  const remove = async (t) => {
    if (!confirm('Soft-delete this tracker iPad? Historical scan records are preserved.')) return;
    try { await api.etechlog.trackersDelete(t.id); await refresh(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-slate-800">Tracker iPads ({trackers.length})</h2>
        {isAdmin && (
          <button
            onClick={() => setShowNew((v) => !v)}
            className="bg-cathay-500 hover:bg-cathay-600 text-white px-3 py-1.5 rounded text-sm"
          >
            {showNew ? 'Cancel' : '+ New tracker'}
          </button>
        )}
      </div>
      {err && <div className="mt-2 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}

      {lastCode && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-4 text-sm">
          <div className="font-medium text-amber-900">
            Pairing code for tracker <span className="font-mono">{lastCode.name}</span>
          </div>
          <div className="text-3xl font-mono mt-2 tracking-widest text-amber-900">{lastCode.code}</div>
          <div className="text-amber-700 mt-2">
            Enter this on the tracker iPad's pairing screen. The code expires in 30 minutes
            and is shown <strong>only once</strong>.
          </div>
        </div>
      )}

      {showNew && (
        <form onSubmit={create} className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="block">
            <span className="block text-xs text-slate-600">Name</span>
            <input
              required value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-600">Location (optional)</span>
            <input
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </label>
          <div className="sm:col-span-2">
            <button
              disabled={busy}
              className="bg-cathay-500 hover:bg-cathay-600 disabled:opacity-60 text-white px-3 py-1.5 rounded text-sm"
            >
              Create + issue pairing code
            </button>
          </div>
        </form>
      )}

      <table className="min-w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-slate-500 text-xs">
            <th className="py-1">Name</th>
            <th className="py-1">Location</th>
            <th className="py-1">Status</th>
            <th className="py-1">Last seen</th>
            <th className="py-1"></th>
          </tr>
        </thead>
        <tbody>
          {trackers.map((t) => (
            <tr key={t.id} className="border-t border-slate-100">
              <td className="py-1.5 font-medium">{t.name}</td>
              <td className="py-1.5 text-slate-600">{t.location || '—'}</td>
              <td className="py-1.5">
                <span className={
                  'text-xs px-2 py-0.5 rounded ' +
                  (t.status === 'active' ? 'bg-green-100 text-green-800' :
                   t.status === 'unpaired' ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-700')
                }>{t.status}</span>
              </td>
              <td className="py-1.5 text-slate-600">{t.last_seen_at ? fmtDateTime(t.last_seen_at) : '—'}</td>
              <td className="py-1.5 text-right space-x-2">
                {isAdmin && (
                  <>
                    <button onClick={() => regen(t)} className="text-cathay-700 hover:underline text-xs">Regenerate code</button>
                    {t.status === 'active' && (
                      <button onClick={() => revoke(t)} className="text-amber-700 hover:underline text-xs">Revoke</button>
                    )}
                    <button onClick={() => remove(t)} className="text-red-600 hover:underline text-xs">Delete</button>
                  </>
                )}
              </td>
            </tr>
          ))}
          {trackers.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-slate-500">No tracker iPads registered yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}

/* ---------------- History ---------------- */

function HistorySection() {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState({ cathay_id: '', etechlog_id: '' });

  const refresh = async () => {
    try {
      const params = { status: 'all' };
      if (filter.cathay_id.trim()) params.cathay_id = filter.cathay_id.trim().toUpperCase();
      if (filter.etechlog_id) params.etechlog_id = filter.etechlog_id;
      const r = await api.etechlog.checkouts(params);
      setRows(r.checkouts);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { refresh(); }, [filter]);

  return (
    <section className="bg-white rounded shadow-sm border p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-medium text-slate-800">Checkout history ({rows.length})</h2>
        <input
          placeholder="Filter by CathayID"
          value={filter.cathay_id}
          onChange={(e) => setFilter((f) => ({ ...f, cathay_id: e.target.value }))}
          className="border border-slate-300 rounded px-2 py-1 text-sm font-mono w-44"
        />
      </div>
      {err && <div className="mt-2 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{err}</div>}
      <table className="min-w-full mt-3 text-sm">
        <thead>
          <tr className="text-left text-slate-500 text-xs">
            <th className="py-1">eTechlog</th>
            <th className="py-1">CathayID</th>
            <th className="py-1">Checked out</th>
            <th className="py-1">Returned</th>
            <th className="py-1">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-slate-100">
              <td className="py-1.5 font-medium">{c.etechlog_label}</td>
              <td className="py-1.5 font-mono">{c.cathay_id}</td>
              <td className="py-1.5 text-slate-600">{fmtDateTime(c.checked_out_at)}</td>
              <td className="py-1.5 text-slate-600">
                {c.returned_at ? fmtDateTime(c.returned_at) : <span className="text-amber-700">still out</span>}
              </td>
              <td className="py-1.5 text-slate-600">
                {c.auto_returned && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">auto-returned</span>}
                {c.overdue_flagged_at && <span className="ml-1 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800">was overdue</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-slate-500">No history yet.</td></tr>
          )}
        </tbody>
      </table>
    </section>
  );
}
